// Tenantra Inc. - Slide-in applicant view: fit score breakdown, credit summary, addresses, work, household
import { useEffect, useState } from 'react';
import { Archive, ArrowLeft, Ban, ChevronRight, RotateCcw, Send } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { DetailSheet, Field, Section, PrimaryAction, SecondaryAction, fmtDate } from './DetailPanel';
import { OfferForm, OnboardingStatus } from './OnboardingOffer';
import { EntityNotes } from './EntityNotes';

export const BAND = {
  Strong: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  Good: 'text-teal-700 bg-teal-50 border-teal-200',
  Fair: 'text-amber-700 bg-amber-50 border-amber-200',
  Weak: 'text-red-700 bg-red-50 border-red-200',
  Blocked: 'text-red-700 bg-red-50 border-red-200',
};
export const WORK = { full_time: 'Full-time', part_time: 'Part-time', not_working: 'Not working', student: 'Student', retired: 'Retired' };
export const fmtPhone = (p) => (p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : p);
const dur = (y, m) => `${y ? `${y} yr ` : ''}${m ? `${m} mo` : ''}`.trim() || 'Under a month';
const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-CA', { maximumFractionDigits: 0 })}`);
const FREQ = { weekly: 'weekly', biweekly: 'bi-weekly', semimonthly: 'twice a month', monthly: 'monthly', annually: 'annually' };
const DOC = { paystub: 'Pay stub', bank_statement: 'Bank statement', savings_statement: 'Savings statement', equifax_report: 'Equifax report' };
const DOC_RESULT = {
  CLEAN: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  CLEAN_PROCEED_WITH_CAUTION: 'text-amber-700 bg-amber-50 border-amber-200',
  UNDETERMINED: 'text-amber-700 bg-amber-50 border-amber-200',
  EDITED: 'text-red-700 bg-red-50 border-red-200',
  Incomplete: 'text-slate-600 bg-slate-50 border-slate-200',
};
const RESULT_LABEL = {
  CLEAN: 'Clean', CLEAN_PROCEED_WITH_CAUTION: 'Caution', UNDETERMINED: 'Undetermined', EDITED: 'Edited', Incomplete: 'Incomplete',
};
const CHECK_LABEL = { wrong_type: 'Wrong document', duplicate: 'Duplicate', unrecognized: 'Unrecognized' };
const CHECK_STYLE = 'text-red-700 bg-red-50 border-red-200';

function DocBlock({ a, onRun, busy }) {
  const review = a.analysis?.doc_review;
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(a.fix?.emailed_at || null);
  const [count, setCount] = useState(a.fix?.sent_count || 0);
  if (!review || review.status !== 'action_required') return null;
  const link = a.fix?.token ? `${window.location.origin}/fix/${a.fix.token}` : '';
  const send = async () => {
    setSending(true);
    try {
      const res = await api.post(`/screening/applications/${a.id}/send-fix`);
      setSentAt(res.data.emailed_at); setCount(res.data.sent_count);
      toast.success(sentAt ? 'Re-upload link resent to applicant' : 'Re-upload link sent to applicant');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the email'); }
    finally { setSending(false); }
  };
  return (
    <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4" data-testid="applicant-doc-block">
      <p className="text-sm font-semibold text-red-800">Scoring paused. Documents need re-upload.</p>
      <p className="text-[13px] text-red-700 mt-1">The Fit Score is held until the applicant uploads the correct documents.</p>
      <ul className="mt-2 space-y-1 text-[13px] text-red-800 list-disc pl-4" data-testid="applicant-doc-block-list">
        {review.rejected.map((r, i) => <li key={i}><span className="font-medium">{r.person_name}:</span> {r.doc_label}. {r.detail}</li>)}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={send} disabled={sending} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-red-600 hover:bg-red-700 text-white text-[12px] font-semibold disabled:opacity-50" data-testid="applicant-send-fix">
          <Send className="w-3.5 h-3.5" />{sending ? 'Sending…' : sentAt ? 'Resend link' : 'Send re-upload link'}
        </button>
        {sentAt && <span className="text-[12px] text-red-700" data-testid="applicant-fix-sent">Sent to applicant {fmtDate(sentAt)}{count > 1 ? ` (${count}×)` : ''}</span>}
        {link && <button onClick={() => { navigator.clipboard?.writeText(link); toast.success('Re-upload link copied'); }} className="text-[12px] font-medium text-red-700 hover:underline" data-testid="applicant-copy-fix">Copy link</button>}
        {link && <button onClick={onRun} disabled={busy} className="text-[12px] font-medium text-slate-600 hover:underline disabled:opacity-50" data-testid="applicant-recheck">{busy ? 'Checking…' : 'Re-check'}</button>}
      </div>
    </div>
  );
}
const VERDICT = {
  strong: ['Strong', 'text-emerald-700 bg-emerald-50 border-emerald-200'],
  marginal: ['Marginal', 'text-amber-700 bg-amber-50 border-amber-200'],
  weak: ['Weak', 'text-red-700 bg-red-50 border-red-200'],
  unknown: ['Not enough data', 'text-slate-600 bg-slate-50 border-slate-200'],
};

function Factors({ fit }) {
  return (
    <ul className="space-y-3" data-testid="applicant-factors">
      {fit.factors.map(f => (
        <li key={f.key} data-testid={`factor-${f.key}`}>
          <div className="flex items-baseline justify-between text-sm"><span className="text-slate-800">{f.label}</span><span className="tabular-nums text-slate-500">{f.points == null ? '—' : `${f.points} / ${f.max}`}</span></div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-teal-600 rounded-full" style={{ width: `${((f.points || 0) / f.max) * 100}%` }} /></div>
          <p className="text-[11px] text-slate-500 mt-1">{f.note}</p>
        </li>
      ))}
    </ul>
  );
}

function Check({ ok, yes, no }) {
  if (ok == null) return <span className="text-slate-500">Not checked</span>;
  return <span className={ok ? 'text-emerald-700' : 'text-amber-700'}>{ok ? yes : no}</span>;
}

function Screening({ a, onRun, busy }) {
  const an = a.analysis;
  if (!an) return (
    <p className="text-sm text-slate-600" data-testid="screening-pending">
      Reading the uploaded pay stubs, bank statement and Equifax report. This is usually ready within a minute of the applicant submitting.{' '}
      <button onClick={onRun} disabled={busy} className="text-teal-700 underline disabled:opacity-50" data-testid="applicant-run-screening">{busy ? 'Reading…' : 'Run now'}</button>
    </p>
  );
  const aff = an.affordability || {};
  const inc = an.income || {};
  const [vl, vc] = VERDICT[aff.verdict] || VERDICT.unknown;
  return (
    <div className="space-y-5" data-testid="screening-results">
      <dl className="grid grid-cols-2 gap-4">
        <Field label="Verified net income">{inc.net_monthly != null ? `${money(inc.net_monthly)}/mo` : 'Not readable'}{inc.pay_frequency ? <span className="text-[11px] text-slate-400"> · paid {FREQ[inc.pay_frequency] || inc.pay_frequency}</span> : null}{inc.paystub_count ? <span className="block text-[11px] text-slate-400">from {inc.verified_doc_count} of {inc.paystub_count} pay stub{inc.paystub_count > 1 ? 's' : ''}</span> : null}</Field>
        <Field label="Rent">{aff.rent != null ? `${money(aff.rent)}/mo` : 'Not set on unit'}</Field>
        <Field label="Income to rent">{aff.income_to_rent != null ? <>{aff.income_to_rent}× <span className={`ml-1 px-1.5 py-0.5 rounded border text-[11px] font-semibold ${vc}`} data-testid="affordability-verdict">{vl}</span></> : '—'}</Field>
        <Field label="Savings runway">{aff.savings_runway_months != null ? `${aff.savings_runway_months} months of rent` : '—'}{aff.savings_offset_applied ? <span className="block text-[11px] text-emerald-700">Savings offset applied</span> : null}</Field>
      </dl>
      {an.household_income?.members?.length ? (
        <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm space-y-0.5" data-testid="household-income">
          <p className="text-[11px] text-slate-500 mb-1">Household income — used for affordability</p>
          <div className="flex justify-between"><span className="text-slate-600 truncate">{a.summary?.name || 'Primary applicant'}</span><span className="tabular-nums text-slate-800">{an.household_income.primary_net != null ? `${money(an.household_income.primary_net)}/mo` : '—'}</span></div>
          {an.household_income.members.map((m, i) => (
            <div key={i} className="flex justify-between"><span className="text-slate-600 truncate">{m.name || `Co-applicant ${i + 1}`}{m.leaseholder ? <span className="text-[10px] text-teal-700"> · leaseholder</span> : null}</span><span className="tabular-nums text-slate-800">{m.net_monthly != null ? `${money(m.net_monthly)}/mo` : 'No verified income'}</span></div>
          ))}
          <div className="flex justify-between border-t border-slate-200 mt-1 pt-1 font-semibold"><span className="text-slate-700">Total</span><span className="tabular-nums text-slate-900">{an.household_income.total_net != null ? `${money(an.household_income.total_net)}/mo` : '—'}</span></div>
        </div>
      ) : null}
      <div className="flex gap-6 text-sm">
        <span>Name on documents: <Check ok={an.name_match} yes="Matches" no="Mismatch" /></span>
        <span>Pay found in bank: <Check ok={an.deposit_match} yes="Yes" no="Not found" /></span>
      </div>
      <div>
        <p className="text-[11px] text-slate-500 mb-2">Documents</p>
        <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="screening-documents">
          {an.documents.map((d, i) => (
            <li key={i} className="px-3 py-2 text-sm" data-testid={`screening-doc-${d.doc_type}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-slate-800">{DOC[d.doc_type] || d.doc_type}<span className="text-[11px] text-slate-400"> · {d.filename}</span></span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {d.check && d.check !== 'ok' && <span className={`px-2 py-0.5 rounded border text-[11px] font-semibold ${CHECK_STYLE}`} data-testid={`doc-check-${d.doc_type}`}>{CHECK_LABEL[d.check] || 'Rejected'}</span>}
                  <span className={`px-2 py-0.5 rounded border text-[11px] font-semibold ${DOC_RESULT[d.result] || DOC_RESULT.Incomplete}`}>{RESULT_LABEL[d.result] || d.result}</span>
                </span>
              </div>
              {d.notes?.filter(Boolean).map((n, j) => <p key={j} className="text-[11px] text-slate-500 mt-0.5">{n}</p>)}
            </li>
          ))}
        </ul>
      </div>
      {an.notes?.length ? (
        <ul className="text-[11px] text-amber-700 list-disc pl-4 space-y-0.5" data-testid="screening-notes">
          {an.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

const REF_STATUS = {
  not_requested: ['Not requested', 'text-slate-500'],
  sent: ['Request sent', 'text-teal-700'],
  opened: ['Opened, awaiting reply', 'text-amber-700'],
  responded: ['Completed', 'text-emerald-700'],
  bounced: ['Email bounced', 'text-red-700'],
};
const PAID = { yes: 'Always on time', mostly: 'Mostly on time', no: 'Often late', not_sure: 'Not sure' };
const ISSUES = { none: 'None reported', minor: 'Minor', major: 'Major' };
const AGAIN = { yes: 'Yes', no: 'No', unsure: 'Unsure' };

function Reference({ a, onRequest, busy }) {
  const ref = a.applicant_reference;
  const rc = a.reference_check || { status: 'not_requested' };
  const resp = rc.response;
  if (!ref || !ref.email) return <p className="text-sm text-slate-600">No reference email was provided on this application.</p>;
  const kind = ref.kind === 'current_landlord' ? 'Landlord' : 'Personal';
  if (resp) return (
    <div className="space-y-4" data-testid="reference-response">
      <dl className="grid grid-cols-2 gap-4">
        <Field label="Reference">{ref.name} · {kind}</Field>
        <Field label="Confirmed name">{resp.confirm_name}</Field>
        <Field label="Confirmed phone">{fmtPhone(resp.confirm_phone)}</Field>
        <Field label="Confirmed address" wide>{resp.confirm_address}</Field>
        <Field label="Paid on time">{PAID[resp.paid_on_time] || resp.paid_on_time}</Field>
        <Field label="Damage / complaints">{ISSUES[resp.issues] || resp.issues}</Field>
        <Field label="Would rent again">{AGAIN[resp.rent_again] || resp.rent_again}</Field>
      </dl>
      {resp.issues_detail && <div><p className="text-[11px] text-slate-500">Issue detail</p><p className="text-sm text-slate-800">{resp.issues_detail}</p></div>}
      {resp.comments && <div><p className="text-[11px] text-slate-500">Comments</p><p className="text-sm text-slate-800">{resp.comments}</p></div>}
    </div>
  );
  const [label] = REF_STATUS[rc.status] || REF_STATUS.not_requested;
  return (
    <div className="space-y-3" data-testid="reference-pending">
      <dl className="grid grid-cols-2 gap-4">
        <Field label="Reference">{ref.name} · {kind}</Field>
        <Field label="Contact">{fmtPhone(ref.phone)} · {ref.email}</Field>
      </dl>
      <p className="text-sm text-slate-600">
        {rc.status === 'not_requested'
          ? 'Email this reference a short questionnaire. The onboarding link can only be sent once they reply.'
          : `${label}. You can send onboarding once they reply.`}
      </p>
      {rc.status !== 'not_requested' && rc.email_sent === false && !rc.test_email && <p className="text-[11px] text-amber-700">The email could not be delivered to this address.</p>}
      <PrimaryAction onClick={onRequest} testId="reference-request-btn">
        {busy ? 'Sending…' : rc.status === 'not_requested' ? 'Request reference' : 'Resend request'}
      </PrimaryAction>
    </div>
  );
}

function DocFiles({ appId, docs, results, onMark, markingId }) {
  const [busy, setBusy] = useState(null);
  if (!docs?.length) return <p className="text-sm text-slate-600">No documents were uploaded.</p>;
  const open = async (d, download) => {
    setBusy(d.file_id + download);
    try {
      const r = await api.get(`/screening/applications/${appId}/documents/${d.file_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (download) { const a = document.createElement('a'); a.href = url; a.download = d.filename || d.doc_type; a.click(); }
      else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(null); }
  };
  return (
    <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="applicant-documents">
      {docs.map((d, i) => {
        const edited = results?.[d.file_id] === 'EDITED';
        return (
          <li key={i} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0"><span className="block text-slate-800 truncate">{DOC[d.doc_type] || d.doc_type}</span><span className="block text-[11px] text-slate-400 truncate">{d.filename}</span></span>
            <span className="flex items-center gap-3 shrink-0">
              <button onClick={() => open(d, false)} disabled={busy} className="text-teal-700 hover:underline disabled:opacity-50" data-testid={`doc-view-${d.file_id}`}>View</button>
              <button onClick={() => open(d, true)} disabled={busy} className="text-slate-500 hover:underline disabled:opacity-50" data-testid={`doc-download-${d.file_id}`}>Download</button>
              <button onClick={() => onMark(d.file_id, !edited)} disabled={markingId === d.file_id} className={`hover:underline disabled:opacity-50 ${edited ? 'text-slate-500' : 'text-red-600'}`} data-testid={`doc-mark-${d.file_id}`}>
                {markingId === d.file_id ? '…' : edited ? 'Unmark' : 'Mark edited'}
              </button>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Credit({ c }) {
  const pending = !c || c.status === 'pending' || !('thin_file' in c);
  if (pending) return <p className="text-sm text-slate-600" data-testid="credit-pending">Pending — read from the applicant's uploaded Equifax report.</p>;
  if (c.thin_file) return <p className="text-sm text-slate-600">Limited credit history on the uploaded report. This is common for newcomers and first-time renters, so it is scored as neutral.</p>;
  return (
    <dl className="grid grid-cols-2 gap-4">
      <Field label="Credit score">{c.score ?? '—'}</Field>
      <Field label="Payments on time">{c.on_time_pct != null ? `${c.on_time_pct}%` : '—'}</Field>
      <Field label="Collections">{c.collections ? `${c.collections} account${c.collections > 1 ? 's' : ''}` : 'None'}</Field>
      <Field label="Bankruptcy / proposal">{c.bankruptcy ? 'Yes' : 'None'}</Field>
      {c.report_date && <Field label="Report date">{fmtDate(c.report_date)}</Field>}
    </dl>
  );
}

function CoApplicantIncome({ an, leaseholder }) {
  if (!an) return <p className="text-sm text-slate-600" data-testid="co-income-pending">Income from this co-applicant's documents will appear once screening has run.</p>;
  const inc = an.income || {};
  return (
    <div className="space-y-4" data-testid="co-income">
      <dl className="grid grid-cols-2 gap-4">
        <Field label="Verified net income">{inc.net_monthly != null ? `${money(inc.net_monthly)}/mo` : 'Not readable'}{inc.pay_frequency ? <span className="text-[11px] text-slate-400"> · paid {FREQ[inc.pay_frequency] || inc.pay_frequency}</span> : null}{inc.paystub_count ? <span className="block text-[11px] text-slate-400">from {inc.verified_doc_count} of {inc.paystub_count} pay stub{inc.paystub_count > 1 ? 's' : ''}</span> : null}</Field>
        <Field label="Counted toward household">{leaseholder ? 'Income, savings & credit' : 'Income only'}</Field>
      </dl>
      <div className="flex gap-6 text-sm">
        <span>Name on documents: <Check ok={an.name_match} yes="Matches" no="Mismatch" /></span>
        <span>Pay found in bank: <Check ok={an.deposit_match} yes="Yes" no="Not found" /></span>
      </div>
      {an.notes?.length ? <ul className="text-[11px] text-amber-700 list-disc pl-4 space-y-0.5" data-testid="co-income-notes">{an.notes.map((n, i) => <li key={i}>{n}</li>)}</ul> : null}
    </div>
  );
}

function CoApplicantView({ co, an, appId, onMark, markingId }) {
  const results = Object.fromEntries((an?.documents || []).filter(d => d.file_id).map(d => [d.file_id, d.result]));
  const cs = an?.credit_summary;
  return (
    <div data-testid="coapplicant-view">
      <div className="mb-3">
        <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] font-medium ${co.leaseholder ? 'bg-teal-50 border-teal-200 text-teal-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-testid="coapplicant-liability">{co.leaseholder ? 'Leaseholder · shares liability' : 'Occupant · income only, no liability'}</span>
      </div>
      <Section title="Income verification" aside={co.leaseholder ? 'Jointly assessed' : 'Employment income only'}>
        <CoApplicantIncome an={an} leaseholder={co.leaseholder} />
      </Section>
      {co.leaseholder && (
        <Section title="Credit" aside="Combined into the household Fit Score">
          {cs && cs.status !== 'unreadable'
            ? <dl className="grid grid-cols-2 gap-4" data-testid="co-credit">
                <Field label="Credit score">{cs.score ?? (cs.thin_file ? 'Thin file' : 'Not readable')}</Field>
                <Field label="On-time payments">{cs.on_time_pct != null ? `${cs.on_time_pct}%` : '—'}</Field>
                <Field label="Collections">{cs.collections || 0}</Field>
                <Field label="Bankruptcy">{cs.bankruptcy ? 'On file' : 'None'}</Field>
              </dl>
            : <p className="text-sm text-slate-600" data-testid="co-credit-pending">Their Equifax report will populate here once screening has run.</p>}
        </Section>
      )}
      <Section title="Uploaded documents" aside={`${(co.documents || []).length} file${(co.documents || []).length !== 1 ? 's' : ''}`}>
        <DocFiles appId={appId} docs={co.documents} results={results} onMark={onMark} markingId={markingId} />
      </Section>
      <Section title="This person">
        <dl className="grid grid-cols-2 gap-4">
          <Field label="Role">{co.leaseholder ? 'Leaseholder' : 'Occupant'}</Field>
          <Field label="Relationship">{co.relationship === 'roommate' ? 'Roommate' : 'Family / partner'}</Field>
          <Field label="Date of birth">{fmtDate(co.date_of_birth)}</Field>
          <Field label="Email">{co.email}</Field>
          <Field label="Phone">{fmtPhone(co.phone)}</Field>
        </dl>
      </Section>
      <Section title="Work">
        <dl className="grid grid-cols-2 gap-4">
          <Field label="Status">{WORK[co.employment.status]}</Field>
          {co.employment.employer && <><Field label="Employer">{co.employment.employer}</Field><Field label="Job title">{co.employment.job_title}</Field><Field label="Time there">{dur(co.employment.years, co.employment.months)}</Field></>}
        </dl>
      </Section>
      <Section title="Addresses" aside={`${co.addresses.length} on application`}>
        <ul className="space-y-2" data-testid="co-addresses">
          {co.addresses.map((x, i) => (
            <li key={i} className="text-sm"><span className="block text-slate-800">{x.unit ? `${x.unit}-` : ''}{x.street}, {x.city}, {x.region}{x.country !== 'Canada' ? `, ${x.country}` : ''}</span><span className="text-[11px] text-slate-400">{i === 0 ? 'Current' : 'Previous'} · {dur(x.years, x.months)}</span></li>
          ))}
        </ul>
      </Section>
      <Section title="Reference & emergency contact">
        <dl className="grid grid-cols-2 gap-4">
          <Field label="Reference">{co.reference?.name} · {co.reference?.kind === 'current_landlord' ? 'Landlord' : 'Personal'}</Field>
          <Field label="Reference contact">{fmtPhone(co.reference?.phone)}</Field>
          <Field label="Emergency contact">{co.emergency_contact?.name} · {co.emergency_contact?.relationship}</Field>
          <Field label="Emergency phone">{fmtPhone(co.emergency_contact?.phone)}</Field>
        </dl>
      </Section>
    </div>
  );
}

function DeclineForm({ name, busy, onConfirm, onCancel }) {
  const [notify, setNotify] = useState(true);
  return (
    <div data-testid="decline-form">
      <Section title="Decline applicant">
        <p className="text-sm text-slate-700">You're about to decline {name}. They'll move to the Declined tab, and their documents and application will be permanently deleted after 90 days.</p>
        <label className="mt-4 flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
          <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} className="mt-0.5" data-testid="decline-notify" />
          <span>Email the applicant a polite decline notice. No reason is shared.</span>
        </label>
        <div className="mt-5 flex gap-2">
          <button onClick={() => onConfirm(notify)} disabled={busy} className="h-10 px-5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="decline-confirm">{busy ? 'Declining…' : 'Confirm decline'}</button>
          <button onClick={onCancel} disabled={busy} className="h-10 px-5 rounded-lg border border-slate-200 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50" data-testid="decline-cancel">Cancel</button>
        </div>
      </Section>
    </div>
  );
}

export function ApplicantPanel({ applicantId, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [screening, setScreening] = useState(false);
  const [offer, setOffer] = useState(false);
  const [coIdx, setCoIdx] = useState(null);
  const load = () => api.get(`/screening/applications/${applicantId}`).then(r => setA(r.data)).catch(e => { toast.error(apiHelpers.handleError(e)); onClose(); });
  useEffect(() => {
    if (!applicantId) return;
    setA(null); setOffer(false); setCoIdx(null); load();
  }, [applicantId]); // eslint-disable-line
  const refresh = () => { setOffer(false); load(); onChanged(); };

  const runScreening = async () => {
    setScreening(true);
    try { await api.post(`/screening/applications/${a.id}/analyze`); toast.success('Screening updated'); load(); onChanged(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setScreening(false); }
  };

  const [refBusy, setRefBusy] = useState(false);
  const requestReference = async () => {
    setRefBusy(true);
    try {
      const r = await api.post(`/references/applications/${a.id}/request`);
      toast.success(r.data.test_email ? 'Request logged (test email not delivered)' : 'Reference request sent');
      load();
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setRefBusy(false); }
  };

  const [markingId, setMarkingId] = useState(null);
  const markEdited = async (fileId, edited) => {
    setMarkingId(fileId);
    try { await api.post(`/screening/applications/${a.id}/documents/${fileId}/mark-edited`, { edited }); toast.success(edited ? 'Marked as edited' : 'Mark removed'); load(); onChanged(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setMarkingId(null); }
  };

  const archive = async (archived) => {
    setBusy(true);
    try { await api.post(`/screening/applications/${a.id}/archive`, { archived }); toast.success(archived ? 'Applicant archived' : 'Applicant restored'); onChanged(); onClose(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };
  const [declining, setDeclining] = useState(false);
  const [declineBusy, setDeclineBusy] = useState(false);
  const decline = async (notify) => {
    setDeclineBusy(true);
    try {
      const r = await api.post(`/screening/applications/${a.id}/decline`, { declined: true, notify });
      toast.success(notify ? (r.data.emailed ? 'Applicant declined and notified' : 'Applicant declined (email could not be sent)') : 'Applicant declined');
      onChanged(); onClose();
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setDeclineBusy(false); }
  };
  const undecline = async () => {
    setBusy(true);
    try { await api.post(`/screening/applications/${a.id}/decline`, { declined: false }); toast.success('Applicant restored to pool'); onChanged(); onClose(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };
  const s = a?.summary;
  const archived = a?.status === 'archived';
  const declined = a?.status === 'declined';
  const refDone = (a?.reference_check || {}).status === 'responded';
  const sendOnboarding = () => refDone ? setOffer(true) : toast.error('Complete the reference check before sending onboarding');
  const coApps = a?.co_applicants || [];
  const co = coIdx != null ? coApps[coIdx] : null;
  const coAn = co ? (a.analysis?.co_applicants || [])[coIdx] : null;
  const coName = co ? [co.first_name, co.last_name].filter(Boolean).join(' ') : '';

  return (
    <DetailSheet open={!!applicantId} onClose={onClose} loading={!a} testId="applicant-panel"
      crumb={co ? 'Co-applicant' : (s && `Screening / ${s.property_address}`)} title={co ? coName : s?.name}
      meta={co
        ? <button onClick={() => setCoIdx(null)} className="text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="coapplicant-back"><ArrowLeft className="w-3.5 h-3.5" /> Back to {s?.name}</button>
        : s && <><span className={`px-2 py-0.5 rounded border text-xs font-semibold tabular-nums ${BAND[s.band]}`} data-testid="applicant-panel-score">{s.fit_score != null ? `${s.fit_score} · ${s.band}` : 'Action needed'}</span><span className="text-slate-500">{s.reference}</span></>}
      footer={!co && a && !offer && !declining && !['onboarding', 'onboarded'].includes(a.status) && (archived
        ? <SecondaryAction onClick={() => !busy && archive(false)} testId="applicant-restore"><RotateCcw className="w-4 h-4" /> Restore to pool</SecondaryAction>
        : declined
        ? <SecondaryAction onClick={() => !busy && undecline()} testId="applicant-restore-declined"><RotateCcw className="w-4 h-4" /> Restore to pool</SecondaryAction>
        : <>
            <PrimaryAction onClick={sendOnboarding} testId="applicant-send-onboarding"><Send className="w-4 h-4" /> Send onboarding</PrimaryAction>
            <SecondaryAction onClick={() => !busy && archive(true)} testId="applicant-archive"><Archive className="w-4 h-4" /> Archive</SecondaryAction>
            <SecondaryAction onClick={() => setDeclining(true)} testId="applicant-decline"><Ban className="w-4 h-4" /> Decline</SecondaryAction>
          </>)}
    >
      {a && co && <CoApplicantView co={co} an={coAn} appId={a.id} onMark={markEdited} markingId={markingId} />}
      {a && !co && offer && <OfferForm app={a} onSent={refresh} onCancel={() => setOffer(false)} />}
      {a && !co && !offer && declining && <DeclineForm name={s?.name} busy={declineBusy} onConfirm={decline} onCancel={() => setDeclining(false)} />}
      {a && !co && !offer && !declining && <>
        {declined && <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="applicant-declined-notice">
          <p className="text-sm font-semibold text-slate-800">Declined{a.declined_at ? ` on ${fmtDate(a.declined_at)}` : ''}</p>
          <p className="text-[13px] text-slate-600 mt-1">This applicant is in the Declined tab. Their documents and application will be permanently deleted on {fmtDate(a.document_purge_at)}.{a.declined_emailed_at ? ' The applicant was notified by email.' : ''}</p>
        </div>}
        {a.status === 'onboarding' && a.onboarding && <OnboardingStatus app={a} onChanged={refresh} />}
        {a.status === 'onboarded' && <Section title="Onboarding"><p className="text-sm text-emerald-700" data-testid="applicant-onboarded">Onboarding complete. Now an active tenant in Unit {a.onboarding?.unit_number}.</p></Section>}
        <DocBlock a={a} onRun={runScreening} busy={screening} />
        <Section title="Tenantra Fit Score" aside={a.fit?.blocked ? 'Paused' : (s.flags.length ? s.flags.join(' · ') : 'No flags')}>
          {a.fit?.blocked
            ? <p className="text-sm text-slate-600" data-testid="fit-blocked">Scoring is paused until the applicant re-uploads the correct documents. See the documents needing attention above.</p>
            : <Factors fit={a.fit} />}
        </Section>
        <Section title="Screening" aside={a.analysis ? <button onClick={runScreening} disabled={screening} className="text-teal-700 hover:underline disabled:opacity-50" data-testid="applicant-rerun-screening">{screening ? 'Reading…' : 'Re-run'}</button> : 'Reads uploaded documents'}>
          <Screening a={a} onRun={runScreening} busy={screening} />
        </Section>
        <Section title="Reference check" aside={(REF_STATUS[(a.reference_check || {}).status] || REF_STATUS.not_requested)[0]}>
          <Reference a={a} onRequest={requestReference} busy={refBusy} />
        </Section>
        <Section title="Uploaded documents" aside={`${(a.documents || []).length} file${(a.documents || []).length !== 1 ? 's' : ''}`}>
          <DocFiles appId={a.id} docs={a.documents} results={Object.fromEntries((a.analysis?.documents || []).filter(d => d.file_id).map(d => [d.file_id, d.result]))} onMark={markEdited} markingId={markingId} />
        </Section>
        <EntityNotes type="applicant" id={a.id} />
        <Section title="Credit file" aside="From applicant's uploaded Equifax report"><Credit c={a.credit_check} /></Section>
        <Section title="Applicant">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Date of birth">{fmtDate(a.applicant.date_of_birth)}</Field>
            <Field label="Move-in">{fmtDate(a.move_in_date)}</Field>
            <Field label="Email">{a.applicant.email}</Field>
            <Field label="Phone">{fmtPhone(a.applicant.phone)}</Field>
          </dl>
        </Section>
        <Section title="Addresses" aside={`${a.addresses.length} on application`}>
          <ul className="space-y-2" data-testid="applicant-addresses">
            {a.addresses.map((x, i) => (
              <li key={i} className="text-sm flex justify-between gap-3">
                <span className="min-w-0"><span className="block text-slate-800 truncate">{x.unit ? `${x.unit}-` : ''}{x.street}, {x.city}, {x.region}{x.country !== 'Canada' ? `, ${x.country}` : ''}</span>
                  <span className="text-[11px] text-slate-400">{i === 0 ? 'Current' : 'Previous'} · {dur(x.years, x.months)}</span></span>
                <span className={`text-[11px] shrink-0 ${a.credit_check.addresses_matched?.[i] === false ? 'text-amber-700' : 'text-emerald-700'}`}>{a.credit_check.addresses_matched?.[i] === false ? 'Not on credit file' : 'Matches file'}</span>
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Work">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Status">{WORK[a.employment.status]}</Field>
            {a.employment.employer && <><Field label="Employer">{a.employment.employer}</Field><Field label="Job title">{a.employment.job_title}</Field><Field label="Time there">{dur(a.employment.years, a.employment.months)}</Field></>}
          </dl>
        </Section>
        <Section title="Household" aside={`${s.adults} adult${s.adults > 1 ? 's' : ''}${s.minors ? `, ${s.minors} under 18` : ''}`}>
          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="applicant-household">
            <li className="px-3 py-2 flex justify-between text-sm"><span className="text-slate-800">{s.name}</span><span className="text-xs text-slate-500">Primary applicant</span></li>
            {coApps.length > 0
              ? coApps.map((x, i) => {
                  const nm = [x.first_name, x.last_name].filter(Boolean).join(' ') || `Co-applicant ${i + 1}`;
                  const net = (a.analysis?.co_applicants || [])[i]?.income?.net_monthly;
                  return (
                    <li key={i}>
                      <button onClick={() => setCoIdx(i)} className="w-full px-3 py-2 flex items-center justify-between gap-2 text-sm hover:bg-slate-50 transition-colors text-left" data-testid={`applicant-coapplicant-${i}`}>
                        <span className="min-w-0"><span className="block text-slate-800 truncate">{nm}</span>
                          <span className="block text-[11px] text-slate-400">{x.leaseholder ? 'Leaseholder' : 'Occupant'} · {x.relationship === 'roommate' ? 'Roommate' : 'Family / partner'}{net != null ? ` · ${money(net)}/mo` : ''}</span></span>
                        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                      </button>
                    </li>
                  );
                })
              : (a.household.adults || []).map((x, i) => (
                  <li key={i} className="px-3 py-2 flex justify-between text-sm"><span className="text-slate-800">{x.full_name}</span>
                    <span className={`text-xs ${x.relationship === 'roommate' ? 'text-amber-700' : 'text-slate-500'}`}>{x.relationship === 'roommate' ? 'Roommate · ID check at onboarding' : 'Family / known'}</span></li>
                ))}
            {(a.household.minors || []).map((m, i) => (
              <li key={`m${i}`} className="px-3 py-2 flex justify-between text-sm"><span className="text-slate-800">{m.full_name}</span><span className="text-xs text-slate-500">Under 18 · {fmtDate(m.date_of_birth)}</span></li>
            ))}
          </ul>
          {coApps.length > 0 && <p className="text-[11px] text-slate-400 mt-2">Open a co-applicant to see their income, documents and application.</p>}
        </Section>
        <Section title="Consent">
          <p className="text-xs text-slate-500">Soft check and household responsibility agreed {fmtDate(a.consent?.agreed_at)} · version {a.consent?.version}</p>
        </Section>
      </>}
    </DetailSheet>
  );
}
